// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '@loa/theme';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Table,
  Icon,
  Message,
  Loader,
  Dropdown,
  Label,
  Segment,
  Form,
  Pagination,
  Confirm
} from 'semantic-ui-react';
import { emailNotificationsAdminApi } from '../api/emailNotificationsAdminApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import {
  EmailNotification,
  NotificationStatus,
  NotificationType,
  NotificationFilters,
  PageResponse
} from '../types/emailNotifications';
import EmailNotificationDetail from '../components/EmailNotificationDetail';

function EmailNotificationList() {
  const [notifications, setNotifications] = useState<EmailNotification[]>([]);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [pageInfo, setPageInfo] = useState<PageResponse<EmailNotification> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [selectedBrokerage, setSelectedBrokerage] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<EmailNotification | null>(null);

  // Retry confirmation
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retryingNotification, setRetryingNotification] = useState<EmailNotification | null>(null);

  const navigate = useNavigate();

  // Load brokerages for filter
  useEffect(() => {
    loadBrokerages();
  }, []);

  const loadBrokerages = async () => {
    try {
      const data = await brokerageApi.getAll();
      setBrokerages(data.content);
    } catch (err) {
      console.error('Failed to load brokerages:', err);
    }
  };

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const filters: NotificationFilters = {
        brokerageId: selectedBrokerage || undefined,
        status: selectedStatus ? (selectedStatus as NotificationStatus) : undefined,
        notificationType: selectedType ? (selectedType as NotificationType) : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      };

      const page = await emailNotificationsAdminApi.listNotifications(filters, {
        page: currentPage,
        size: pageSize
      });

      setNotifications(page.content);
      setPageInfo(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [selectedBrokerage, selectedStatus, selectedType, startDate, endDate, currentPage, pageSize]);

  // Load notifications when filters or page changes
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const clearFilters = () => {
    setSelectedBrokerage('');
    setSelectedStatus('');
    setSelectedType('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
  };

  const handleViewDetails = (notification: EmailNotification) => {
    setSelectedNotification(notification);
    setDetailModalOpen(true);
  };

  const handleRetryClick = (notification: EmailNotification) => {
    setRetryingNotification(notification);
    setRetryConfirmOpen(true);
  };

  const handleRetryConfirm = async () => {
    if (!retryingNotification) return;

    try {
      setError(null);
      await emailNotificationsAdminApi.retryNotification(retryingNotification.id);
      setSuccessMessage(
        `Notification scheduled for retry: ${retryingNotification.recipientEmail}`
      );
      setRetryConfirmOpen(false);
      setRetryingNotification(null);
      // Reload notifications
      await loadNotifications();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry notification');
      setRetryConfirmOpen(false);
      setRetryingNotification(null);
    }
  };

  const handleRetryCancel = () => {
    setRetryConfirmOpen(false);
    setRetryingNotification(null);
  };

  const getStatusColor = (status: NotificationStatus): 'green' | 'red' | 'yellow' | 'blue' | 'grey' => {
    switch (status) {
      case NotificationStatus.SENT:
        return 'green';
      case NotificationStatus.FAILED:
        return 'red';
      case NotificationStatus.PENDING:
        return 'yellow';
      case NotificationStatus.RETRY_SCHEDULED:
        return 'blue';
      default:
        return 'grey';
    }
  };

  const getNotificationTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.ADVISOR_PROGRESS:
        return 'Progress';
      case NotificationType.ADVISOR_COMPLETION:
        return 'Completion';
      case NotificationType.PROVIDER_NOTIFICATION:
        return 'Provider';
      default:
        return type;
    }
  };

  const canRetry = (notification: EmailNotification): boolean => {
    return (
      (notification.status === NotificationStatus.FAILED ||
        notification.status === NotificationStatus.RETRY_SCHEDULED) &&
      notification.retryCount < notification.maxRetries
    );
  };


  const getStatusIconName = (status: NotificationStatus): 'check circle' | 'times circle' | 'clock' | 'hourglass half' => {
    switch (status) {
      case NotificationStatus.SENT:
        return 'check circle';
      case NotificationStatus.FAILED:
        return 'times circle';
      case NotificationStatus.RETRY_SCHEDULED:
        return 'clock';
      default:
        return 'hourglass half';
    }
  };

  const formatSentOrFailedTime = (notification: EmailNotification): string => {
    if (notification.sentAt) {
      return formatDateTime(notification.sentAt);
    }
    if (notification.status === NotificationStatus.FAILED) {
      return formatDateTime(notification.updatedAt);
    }
    return 'N/A';
  };

  const brokerageOptions = [
    { key: '', text: 'All Brokerages', value: '' },
    ...brokerages.map((b) => ({ key: b.id, text: b.name, value: b.id }))
  ];

  const statusOptions = [
    { key: '', text: 'All Statuses', value: '' },
    { key: 'SENT', text: 'Sent', value: 'SENT' },
    { key: 'FAILED', text: 'Failed', value: 'FAILED' },
    { key: 'PENDING', text: 'Pending', value: 'PENDING' },
    { key: 'RETRY_SCHEDULED', text: 'Retry Scheduled', value: 'RETRY_SCHEDULED' }
  ];

  const typeOptions = [
    { key: '', text: 'All Types', value: '' },
    {
      key: 'ADVISOR_PROGRESS',
      text: 'Advisor Progress',
      value: 'ADVISOR_PROGRESS'
    },
    {
      key: 'ADVISOR_COMPLETION',
      text: 'Advisor Completion',
      value: 'ADVISOR_COMPLETION'
    },
    {
      key: 'PROVIDER_NOTIFICATION',
      text: 'Provider Notification',
      value: 'PROVIDER_NOTIFICATION'
    }
  ];

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='list' />
        <Header.Content>
          Email Notifications
          <Header.Subheader>
            View and manage email notification history
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Button
        color='teal'
        onClick={() => navigate('/email-notifications')}
        style={{ marginBottom: '20px' }}
      >
        <Icon name='arrow left' />
        Back to Dashboard
      </Button>

      {/* Filters */}
      <Segment>
        <Header as='h3'>
          <Icon name='filter' />
          Filters
        </Header>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='filter-brokerage'>Brokerage</label>
              <Dropdown
                id='filter-brokerage'
                fluid
                selection
                search
                options={brokerageOptions}
                value={selectedBrokerage}
                onChange={(_e, data) => {
                  setSelectedBrokerage(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by brokerage'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-status'>Status</label>
              <Dropdown
                id='filter-status'
                fluid
                selection
                options={statusOptions}
                value={selectedStatus}
                onChange={(_e, data) => {
                  setSelectedStatus(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by status'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-type'>Type</label>
              <Dropdown
                id='filter-type'
                fluid
                selection
                options={typeOptions}
                value={selectedType}
                onChange={(_e, data) => {
                  setSelectedType(data.value as string);
                  setCurrentPage(0);
                }}
                placeholder='Filter by type'
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='filter-start-date'>Start Date</label>
              <input
                id='filter-start-date'
                type='datetime-local'
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='filter-end-date'>End Date</label>
              <input
                id='filter-end-date'
                type='datetime-local'
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </Form.Field>
          </Form.Group>
          <Button size='small' onClick={clearFilters}>
            <Icon name='remove' />
            Clear Filters
          </Button>
        </Form>
      </Segment>

      {successMessage && (
        <Message positive onDismiss={() => setSuccessMessage(null)}>
          <Message.Header>Success</Message.Header>
          <p>{successMessage}</p>
        </Message>
      )}

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading notifications...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Recipient</Table.HeaderCell>
                <Table.HeaderCell>Subject</Table.HeaderCell>
                <Table.HeaderCell>Created</Table.HeaderCell>
                <Table.HeaderCell>Sent/Failed</Table.HeaderCell>
                <Table.HeaderCell>Retry</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {notifications.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='8' textAlign='center'>
                    No notifications found
                  </Table.Cell>
                </Table.Row>
              ) : (
                notifications.map((notification) => (
                  <Table.Row key={notification.id}>
                    <Table.Cell>
                      <Label color={getStatusColor(notification.status)} size='small'>
                        <Icon name={getStatusIconName(notification.status)} />
                        {notification.status}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color='blue' size='small'>
                        {getNotificationTypeLabel(notification.notificationType)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {notification.recipientEmail}
                      {notification.hasAttachment && (
                        <Icon name='paperclip' style={{ marginLeft: '5px' }} />
                      )}
                    </Table.Cell>
                    <Table.Cell style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {notification.subject}
                    </Table.Cell>
                    <Table.Cell>{formatDateTime(notification.createdAt)}</Table.Cell>
                    <Table.Cell>
                      {formatSentOrFailedTime(notification)}
                    </Table.Cell>
                    <Table.Cell textAlign='center'>
                      {notification.retryCount > 0 && (
                        <Label size='tiny'>
                          {notification.retryCount}/{notification.maxRetries}
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign='center'>
                      <Button.Group size='small'>
                        <Button
                          icon='eye'
                          color='teal'
                          onClick={() => handleViewDetails(notification)}
                          title='View Details'
                        />
                        {canRetry(notification) && (
                          <Button
                            icon='redo'
                            color='orange'
                            onClick={() => handleRetryClick(notification)}
                            title='Retry'
                          />
                        )}
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          {/* Pagination */}
          {pageInfo && pageInfo.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <Pagination
                activePage={currentPage + 1}
                totalPages={pageInfo.totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
                firstItem={{ content: <Icon name='angle double left' />, icon: true }}
                lastItem={{ content: <Icon name='angle double right' />, icon: true }}
                prevItem={{ content: <Icon name='angle left' />, icon: true }}
                nextItem={{ content: <Icon name='angle right' />, icon: true }}
              />
            </div>
          )}

          <div style={{ marginTop: '20px', color: '#666', textAlign: 'center' }}>
            {pageInfo && (
              <>
                Showing {pageInfo.number * pageInfo.size + 1} -{' '}
                {Math.min((pageInfo.number + 1) * pageInfo.size, pageInfo.totalElements)} of{' '}
                {pageInfo.totalElements} notification{pageInfo.totalElements === 1 ? '' : 's'}
              </>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedNotification && (
        <EmailNotificationDetail
          notification={selectedNotification}
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedNotification(null);
          }}
          onRetry={() => {
            setDetailModalOpen(false);
            handleRetryClick(selectedNotification);
          }}
        />
      )}

      {/* Retry Confirmation */}
      <Confirm
        open={retryConfirmOpen}
        header='Retry Notification'
        content={
          retryingNotification
            ? `Are you sure you want to retry sending this notification to ${retryingNotification.recipientEmail}?`
            : ''
        }
        confirmButton='Retry'
        onCancel={handleRetryCancel}
        onConfirm={handleRetryConfirm}
      />
    </Container>
  );
}

export default EmailNotificationList;
